import { csvCell } from '~/utils/csvCell';

import { salesColumns, salesReadings } from './SupplementalViews';
import type { MarketingPresentation } from './liveDataAdapter';
import {
  type Filters,
  type Row,
  buildTable,
  productRows,
  toAttributionCSV,
  toProductCSV,
  toTableCSV,
} from './presentationModel';

/** Key/value scope rows written above every export, matching the Contribution Margin and KPI exports. */
export function scopeHeader(filters: Filters): string {
  return [
    ['Report', 'Performance Marketing'],
    ['View', filters.view],
    ['From', filters.from],
    ['To', filters.to],
    ['Country', filters.country],
    ['Attribution model', filters.attribution],
    ['Metric', filters.metric],
    ['Source', 'lux-analytics-prod.analytics.perf_mkt_reporting_roas'],
  ]
    .map((row) => row.map((cell) => csvCell(cell, 'Unknown')).join(','))
    .join('\r\n');
}

/** Download name carries the view, the filtered country and the dates. */
export function marketingCsvFilename(filters: Filters): string {
  const slug = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  const country = filters.country && filters.country !== 'All' ? `-${slug(filters.country)}` : '';
  return `performance-marketing-${slug(filters.view)}${country}-${filters.from}-${filters.to}.csv`;
}

/** One complete settled source population is shared by display, host and every export. */
export function marketingCSV(
  model: MarketingPresentation,
  filters: Filters,
  ready: boolean,
  identity?: string,
): string {
  return scopeHeader(filters) + '\r\n' + marketingRows(model, filters, ready, identity);
}

function marketingRows(model: MarketingPresentation, filters: Filters, ready: boolean, identity?: string): string {
  if (!ready || !identity || model.identity !== identity) {
    throw new Error('Wait for complete current data before exporting.');
  }
  const context = { 'Sydney as of': model.asOf, 'Effective source scope': model.sourceScope };
  if (filters.view === 'Attribution') {
    return toAttributionCSV(model.attribution, filters, context);
  }
  if (filters.view === 'Product mix') {
    return toProductCSV(productRows(model.summary), filters, context);
  }
  if (filters.view === 'Sales') {
    const rows = salesReadings(filters.timeSplit === 'Date Cadence' ? model.salesDaily : model.sales),
      totals = salesReadings(model.salesTotal),
      priorRows = salesReadings(filters.timeSplit === 'Date Cadence' ? model.salesPreviousDaily : model.salesPrevious),
      priorTotals = salesReadings(model.salesPreviousTotal),
      cols = salesColumns([...rows, ...totals, ...priorRows, ...priorTotals]);
    return [
      [
        'Period',
        'Scope',
        'Data Split 1',
        'Data Split 2',
        ...cols,
        'From',
        'To',
        'Sydney as of',
        'Effective source scope',
      ],
      ...[
        ...totals.map((row) => ({ period: 'Current', scope: 'Independent global distinct total', ...row })),
        ...rows.map((row) => ({ period: 'Current', scope: 'Selected split group', ...row })),
        ...priorTotals.map((row) => ({ period: 'Previous', scope: 'Independent global distinct total', ...row })),
        ...priorRows.map((row) => ({ period: 'Previous', scope: 'Selected split group', ...row })),
      ].map((row: Row) => [
        row.period,
        row.scope,
        row.split_one,
        row.split_two,
        ...cols.map((column) => row[column]),
        filters.from,
        filters.to,
        model.asOf,
        model.sourceScope,
      ]),
    ]
      .map((row) => row.map((cell) => csvCell(cell, 'Unknown')).join(','))
      .join('\r\n');
  }
  if (filters.view === 'Monthly' || filters.view === 'PoP') {
    const rows =
      filters.view === 'Monthly'
        ? model.monthly
        : [
            ...model.daily.map((row) => ({ ...row, period: 'Current' })),
            ...model.previous.map((row) => ({ ...row, period: 'Previous' })),
          ];
    const cols = [...new Set(rows.flatMap((row) => Object.keys(row)))];
    return [
      [...cols, 'From', 'To', 'Sydney as of', 'Effective source scope'],
      ...rows.map((row) => [...cols.map((col) => row[col]), filters.from, filters.to, model.asOf, model.sourceScope]),
    ]
      .map((row) => row.map((cell) => csvCell(cell, 'Unknown')).join(','))
      .join('\r\n');
  }
  return toTableCSV(
    buildTable(filters.timeSplit === 'Date Cadence' ? model.daily : model.summary, filters),
    filters,
    context,
  );
}
